import React, { useMemo, useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { clone } from "lodash";
import { Icon } from "uikit";

import { useTypedSelector } from "../../../../../../../../../../redux/store";
import {
	useColumns,
	useVisibleColumns,
	useWidths,
} from "../../../../../../../../../../hooks/useTableSettings";
import { makeLookupTable } from "../../../../../../../../../../utils";
import { ColumnSetupModal } from "../../../../../../../../../../components";
import ModelTable from "../../../../../../../../../../components/OrderPageWidgets/OrderTable/components/ModelTable";
import columnBase, {
	ColumnId,
	ColumnId as LocalColumnId,
	defaultColumnIds,
} from "../../../../../../../../../../components/OrderPageWidgets/OrderTable/components/ModelTable/columns";
import { orderTableByType } from "../../../../../../../../../../components/OrderPageWidgets/OrderTable";
import { Button } from "..";

const ButtonsTableSettings: React.FC<ButtonsTableSettings.Props> = () => {
	const { t } = useTranslation();

	const ordersType = useTypedSelector(
		(state) => state.ordersPageReducer.ordersType,
	);
	const hasActiveOrder = useTypedSelector(
		(state) => Boolean(state.ordersPageReducer.activeOrder),
	);
	const tableId = useMemo(
		() => `main-order-page-${ordersType}1`,
		[ordersType],
	);

	const tableType = useMemo(() => orderTableByType[ordersType], [ordersType]);
	const columns = useColumns(tableType);
	const { columnIds, setColumnIds } = useVisibleColumns(tableId, tableType);
	const { widths, setWidths } = useWidths(tableId, tableType);

	const modelTableColumns = useMemo<ModelTable.Column[]>(
		() =>
			columns
				.map((column) => ({
					id: column.id as ColumnId,
					width: widths[column.id],
					visible: columnIds.includes(column.id as ColumnId),
				}))
				.sort((leftColumn, rightColumn) => {
					const leftIndex = columnIds.indexOf(leftColumn.id);
					const rightIndex = columnIds.indexOf(rightColumn.id);

					return leftIndex - rightIndex;
				}),
		[columnIds, columns, widths],
	);
	const tableSettingsValue = useMemo(
		() =>
			modelTableColumns
				.filter((column) => column.visible)
				.map((column) => column.id),
		[modelTableColumns],
	);

	const modelTableOnChangeColumns = useCallback(
		(columns: ModelTable.Column[]) => {
			setColumnIds(
				columns
					.filter((column) => column.visible)
					.map((column) => column.id),
			);

			setWidths(
				columns.reduce((accumulator, column) => {
					accumulator[column.id] = column.width || 100;

					return accumulator;
				}, {} as Record<ColumnId, number>),
			);
		},
		[setColumnIds, setWidths],
	);

	const columnLookup = useMemo(
		() => makeLookupTable(modelTableColumns, (column) => column.id, true),
		[modelTableColumns],
	);

	const tableSettingsOnChange = useCallback(
		(columnIds: string[]) => {
			const otherColumns = modelTableColumns
				.filter((column) => !columnIds.includes(column.id))
				.map((column) => ({ ...column, visible: false }));

			const newColumns = columnIds.map((columnId) => ({
				id: columnId as LocalColumnId,
				width: columnLookup[columnId]?.value.width,
				visible: true,
			}));

			const a = [...newColumns, ...otherColumns];

			modelTableOnChangeColumns(a);
		},
		[columnLookup, modelTableColumns, modelTableOnChangeColumns],
	);

	const [showColumnModal, setShowColumnModal] = useState(false);

	const resetButtonOnClick = useCallback(
		() =>
			defaultColumnIds &&
			tableSettingsOnChange?.(clone(defaultColumnIds)),
		[tableSettingsOnChange],
	);
	const editButtonOnClick = useCallback(() => setShowColumnModal(true), []);

	const columnSetupModalOnClose = useCallback(
		() => setShowColumnModal(false),
		[],
	);

	const columnSetupModalOnSubmit = useCallback(
		(columnSetupModalValue: ColumnSetupModal.Value) => {
			tableSettingsOnChange?.(columnSetupModalValue);
			setShowColumnModal(false);
		},
		[tableSettingsOnChange],
	);

	const tableSettingColumns = useMemo(
		() =>
			defaultColumnIds.map((defaultColumnId) => {
				const lineTPath: string[] = [
					columnBase[defaultColumnId]?.pathTranslation,
					columnBase[defaultColumnId]?.label,
					"",
				];

				if (
					defaultColumnId === ColumnId.TimeToDelivery &&
					[orderTableByType.live, orderTableByType.preliminary].find(
						(tab) => tab === tableType,
					)
				) {
					lineTPath.unshift(
						"orderPageWidgets.orderTable.modelTable.columns.timeLeft",
					);
				}

				return {
					id: defaultColumnId,
					label: t(lineTPath),
				};
			}),
		[t, tableType],
	);

	return (
		<>
			<Button
				title={t("tableSettings.str0") ?? ""}
				active={false}
				tabIndex={-1}
				onClick={resetButtonOnClick}
				disabled={!hasActiveOrder || !ordersType}
			>
				<Icon id="reset" size={20} />
			</Button>
			<Button
				title={t("tableSettings.str1") ?? ""}
				active={false}
				tabIndex={-1}
				onClick={editButtonOnClick}
				disabled={!hasActiveOrder || !ordersType}
			>
				<Icon id="table-edit" size={20} />
			</Button>

			{showColumnModal && (
				<ColumnSetupModal
					columns={tableSettingColumns}
					disabledColumns={[]}
					value={tableSettingsValue}
					onClose={columnSetupModalOnClose}
					onSubmit={columnSetupModalOnSubmit}
				/>
			)}
		</>
	);
};

declare namespace ButtonsTableSettings {
	interface Props {}
}

export default ButtonsTableSettings;
